import React from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------
   IMAGE PATHS  (files live in  src/assets/images/ )
   Put your pictures in that folder with the SAME file names below.
   If a picture is missing the page still works (navy background shows).
------------------------------------------------------------------- */
const imageFiles = require.context("../assets/images", false, /\.(png|jpe?g|webp|svg)$/);
const pic = (name) => {
  try { return imageFiles(`./${name}`); } catch (e) { return undefined; }
};
const images = {
  banner: pic("services-banner.png"),           // 1920 x 600
  lettings: pic("services-lettings.png"),       // 1100 x 1000
  maintenance: pic("services-maintenance.png"), // 1100 x 1000
  cleaning: pic("services-cleaning.png"),       // 1100 x 1000
  cta: pic("home-cta.png"),                     // same picture as Home page CTA (1920 x 700)
};
const bg = (overlay, img) => (img ? `${overlay}, url(${img})` : overlay);

/* ------------------------------------------------------------------
   ICONS (inline SVG, no extra packages needed)
------------------------------------------------------------------- */
const paths = {
  key: (<><circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.8 12.2 21 2M17 6l3 3M14 9l2.5 2.5" /></>),
  wrench: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />,
  sparkles: (<><path d="M11 3l1.9 5.1L18 10l-5.1 1.9L11 17l-1.9-5.1L4 10l5.1-1.9z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" /></>),
  building: (<><rect x="4" y="3" width="16" height="18" rx="1" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M10 21v-3h4v3" /></>),
  home: <path d="M3 11l9-8 9 8M5 10v10h14V10M10 20v-6h4v6" />,
  shield: (<><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 12l2 2 4-4" /></>),
  users: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></>),
  pound: <path d="M18 7c0-5.333-8-5.333-8 0M10 7v14M6 21h12M6 13h10" />,
  fileCheck: (<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 14l2 2 4-4" /></>),
  calendar: (<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" /></>),
  flame: <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.4-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5z" />,
  zap: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  clipboard: (<><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 14l2 2 4-4" /></>),
  briefcase: (<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18" /></>),
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />,
};

function Icon({ name, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

/* ------------------------------------------------------------------
   PAGE CONTENT
------------------------------------------------------------------- */
const services = [
  {
    id: "lettings",
    number: "01",
    title: "Property Rental and Lettings",
    sic: "SIC 98000",
    icon: "key",
    image: images.lettings,
    alt: "Property rental and lettings",
    intro: "We maximise rental yields for landlords while ensuring a smooth, fully compliant experience for tenants.",
    features: [
      { icon: "users", title: "Tenant Sourcing and Vetting", text: "Comprehensive background, credit and employment checks." },
      { icon: "pound", title: "Rent Collection and Financials", text: "Secure monthly tracking, direct deposits and transparent financial statements." },
      { icon: "fileCheck", title: "Tenancy Administration", text: "Legally compliant tenancy agreements, deposit protection (TDS) and check-in/check-out inventories." },
    ],
  },
  {
    id: "maintenance",
    number: "02",
    title: "Property Maintenance and Handyman Work",
    sic: "SIC 81100",
    icon: "wrench",
    image: images.maintenance,
    alt: "Property maintenance and handyman work",
    intro: "Fast, reliable maintenance fixes to protect the fabric of your building.",
    features: [
      { icon: "wrench", title: "General Property Repairs", text: "Carpentry, minor plumbing, electrical fixes, painting and decorating." },
      { icon: "calendar", title: "Planned Upkeep", text: "Scheduled roof checks, gutter cleaning and preventative building maintenance." },
      { icon: "shield", title: "Safety Compliance", text: "Arranging mandatory UK landlord certificates, including Gas Safety (CP12), EICR and Fire Risk Assessments." },
    ],
    chips: [
      { icon: "flame", text: "Gas Safety (CP12)" },
      { icon: "zap", text: "EICR" },
      { icon: "clipboard", text: "Fire Risk Assessments" },
    ],
  },
  {
    id: "cleaning",
    number: "03",
    title: "Professional Cleaning Services",
    sic: "SIC 81100",
    icon: "sparkles",
    image: images.cleaning,
    alt: "Professional cleaning services",
    intro: "Spotless cleaning standards delivered by professional teams.",
    features: [
      { icon: "sparkles", title: "End-of-Tenancy Cleaning", text: "Deep cleans designed to help tenants secure deposit returns and landlords prepare for new lets." },
      { icon: "building", title: "Commercial and Office Cleaning", text: "Flexible, out-of-hours cleaning schedules for office buildings, retail shops and communal blocks." },
      { icon: "home", title: "Regular Domestic Cleaning", text: "Weekly or fortnightly domestic cleaning tailored to individual homes." },
    ],
  },
];

const audience = [
  { icon: "key", title: "Landlords" },
  { icon: "users", title: "Tenants" },
  { icon: "home", title: "Homeowners" },
  { icon: "briefcase", title: "Business owners" },
];

/* ------------------------------------------------------------------
   PAGE CSS (everything for the Services page is in this file)
------------------------------------------------------------------- */
const css = `
.sv-banner{background-color:var(--navy-deep);background-size:cover;background-position:center;color:#fff;padding-top:100px;padding-bottom:90px}
.sv-banner-inner{max-width:760px;animation:svRise .8s ease-out both}
@keyframes svRise{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
.sv-banner h1{color:#fff;font-size:clamp(32px,4.8vw,52px);font-weight:800;letter-spacing:-.5px;margin-bottom:16px}
.sv-banner p{font-size:20px;font-style:italic;color:#DCE3EA;margin:0 0 30px;max-width:620px}
.sv-jump{display:flex;gap:12px;flex-wrap:wrap}
.sv-jump a{display:inline-flex;align-items:center;gap:10px;padding:11px 20px;border:1.5px solid rgba(255,255,255,.55);border-radius:40px;color:#fff;text-decoration:none;font-family:var(--font-head);font-weight:600;font-size:15px;transition:background .2s,color .2s,border-color .2s}
.sv-jump a:hover{background:#fff;color:var(--navy);border-color:#fff}

.sv-ico{flex:0 0 auto;width:48px;height:48px;border-radius:50%;background:var(--mist);color:var(--navy);display:grid;place-items:center}

.sv-block{padding-top:96px;padding-bottom:96px;scroll-margin-top:90px}
.sv-block:nth-of-type(even){background:var(--paper)}
.sv-grid{display:grid;grid-template-columns:1fr 1.1fr;gap:72px;align-items:center}
.sv-block.reverse .sv-grid{grid-template-columns:1.1fr 1fr}
.sv-block.reverse .sv-media{order:2}
.sv-media{position:relative;padding:0 0 28px 28px}
.sv-block.reverse .sv-media{padding:0 28px 28px 0}
.sv-media::before{content:"";position:absolute;left:0;bottom:0;width:70%;height:70%;background:var(--navy);border-radius:6px}
.sv-block.reverse .sv-media::before{left:auto;right:0}
.sv-media img{position:relative;width:100%;aspect-ratio:11/10;object-fit:cover;border-radius:6px;background:var(--mist)}
.sv-num{font-family:var(--font-head);font-weight:800;font-size:56px;line-height:1;color:var(--silver);opacity:.7;margin-bottom:6px}
.sv-title{display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin-bottom:12px}
.sv-title h2{font-size:clamp(26px,3.2vw,36px);font-weight:800;margin:0}
.sv-sic{font-family:var(--font-head);font-weight:600;font-size:13px;letter-spacing:.3px;color:var(--navy);background:var(--mist);border:1px solid #D6DDE4;padding:4px 12px;border-radius:30px}
.sv-intro{color:var(--muted);font-size:18px;margin-bottom:28px}
.sv-features{display:grid;gap:14px}
.sv-feature{display:flex;gap:16px;padding:20px 22px;background:#fff;border:1px solid #E1E6EB;border-radius:6px;transition:box-shadow .2s,border-color .2s}
.sv-feature:hover{box-shadow:0 12px 28px rgba(15,34,56,.1);border-color:var(--silver)}
.sv-feature .sv-ico{background:var(--navy);color:#fff}
.sv-feature h3{font-size:18px;margin-bottom:4px}
.sv-feature p{margin:0;color:var(--muted);font-size:16px;line-height:1.55}
.sv-chips{display:flex;gap:10px;flex-wrap:wrap;margin-top:20px}
.sv-chip{display:inline-flex;align-items:center;gap:8px;padding:8px 14px;border-radius:4px;background:var(--navy);color:#fff;font-family:var(--font-head);font-weight:600;font-size:14px}
.sv-chip svg{color:var(--gold)}
.sv-more{margin-top:26px}

.sv-aud{background:var(--navy);color:#fff;padding-top:80px;padding-bottom:80px}
.sv-aud-grid{display:grid;grid-template-columns:1fr 1.3fr;gap:56px;align-items:center}
.sv-aud h2{color:#fff;font-size:clamp(26px,3.2vw,36px);font-weight:800}
.sv-aud p{color:#C4CFDA;margin:0;font-size:18px}
.sv-aud-items{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}
.sv-aud-item{display:flex;flex-direction:column;align-items:center;gap:12px;padding:24px 12px;border:1px solid rgba(185,194,204,.3);border-radius:6px;background:rgba(255,255,255,.04);font-family:var(--font-head);font-weight:600;font-size:15.5px;text-align:center}
.sv-aud-item svg{color:var(--gold)}

.sv-cta{padding-top:100px;padding-bottom:100px;text-align:center;color:#fff;background-color:var(--navy-deep);background-size:cover;background-position:center}
.sv-cta h2{color:#fff;font-size:clamp(28px,4vw,42px);font-weight:800;max-width:760px;margin:0 auto 16px}
.sv-cta p{color:#DCE3EA;font-size:19px;max-width:560px;margin:0 auto 34px}
.sv-cta-btns{display:flex;gap:14px;justify-content:center;flex-wrap:wrap}
.sv-cta .btn svg{vertical-align:-5px;margin-right:8px}

@media (max-width:980px){
  .sv-banner{padding-top:76px;padding-bottom:70px}
  .sv-block{padding-top:72px;padding-bottom:72px}
  .sv-grid,.sv-block.reverse .sv-grid{grid-template-columns:1fr;gap:44px}
  .sv-block.reverse .sv-media{order:0}
  .sv-aud-grid{grid-template-columns:1fr;gap:36px}
}
@media (max-width:560px){
  .sv-aud-items{grid-template-columns:1fr 1fr}
  .sv-feature{flex-direction:column;gap:12px}
}
`;

/* ------------------------------------------------------------------
   COMPONENT
------------------------------------------------------------------- */
export default function Services() {
  return (
    <>
      <style>{css}</style>

      {/* BANNER */}
      <section
        className="sv-banner"
        style={{ backgroundImage: bg("linear-gradient(90deg, rgba(15,34,56,.93) 0%, rgba(15,34,56,.75) 55%, rgba(15,34,56,.45) 100%)", images.banner) }}
      >
        <div className="container">
          <div className="sv-banner-inner">
            <h1>Our Expert Services</h1>
            <p>Professional care tailored for homes, rentals, and commercial businesses.</p>
            <nav className="sv-jump" aria-label="Jump to a service">
              {services.map((s) => (
                <a href={`#${s.id}`} key={s.id}>
                  <Icon name={s.icon} size={18} />
                  {s.title.split(" and ")[0]}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      {services.map((s, i) => (
        <section id={s.id} key={s.id} className={"sv-block" + (i % 2 === 1 ? " reverse" : "")}>
          <div className="container sv-grid">
            <div className="sv-media">
              <img src={s.image} alt={s.alt} />
            </div>
            <div>
              <div className="sv-num">{s.number}</div>
              <div className="sv-title">
                <h2>{s.title}</h2>
                <span className="sv-sic">{s.sic}</span>
              </div>
              <p className="sv-intro">{s.intro}</p>
              <div className="sv-features">
                {s.features.map((f) => (
                  <div className="sv-feature" key={f.title}>
                    <div className="sv-ico"><Icon name={f.icon} /></div>
                    <div>
                      <h3>{f.title}</h3>
                      <p>{f.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              {s.chips && (
                <div className="sv-chips">
                  {s.chips.map((c) => (
                    <span className="sv-chip" key={c.text}><Icon name={c.icon} size={16} />{c.text}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* WHO WE WORK WITH */}
      <section className="sv-aud">
        <div className="container sv-aud-grid">
          <div>
            <h2>Who We Work With</h2>
            <p>Reliable property management and facility care for landlords, tenants, homeowners and business owners.</p>
          </div>
          <div className="sv-aud-items">
            {audience.map((a) => (
              <div className="sv-aud-item" key={a.title}>
                <Icon name={a.icon} size={30} />
                {a.title}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section
        className="sv-cta"
        style={{ backgroundImage: bg("linear-gradient(rgba(15,34,56,.86), rgba(15,34,56,.86))", images.cta) }}
      >
        <div className="container">
          <h2>Ready to take the hassle out of property care?</h2>
          <p>Tell us what you need and we will come back to you with a clear quote.</p>
          <div className="sv-cta-btns">
            <Link to="/contact" className="btn btn-gold">Get a Free Quote</Link>
            <a href="tel:07459262621" className="btn btn-outline"><Icon name="phone" size={18} />07459 262621</a>
          </div>
        </div>
      </section>
    </>
  );
}